// Copyright (c) Bentley Systems
import * as React from "react";

/**
 * Persisted tree view state that survives navigation and refresh
 */
export type PersistedEtreeViewState = {
  routePath: string;
  routeTreeId: string;
  routeTreeParams: string;
  selectedNodeId: string | null;
  expandedNodes: Record<string, boolean>;
  appliedSearchTerm: string;
  searchQuery: string;
  scrollTop: number;
  // Expansion-intent model
  policyExpandedCandidates?: Record<string, boolean>;
  manualExpanded?: Record<string, boolean>;
  manualCollapsed?: Record<string, boolean>;
};

export type EtreePersistenceContextValue = {
  loadPersistedState: (storageKey: string) => PersistedEtreeViewState | null;
  persistState: (storageKey: string, state: PersistedEtreeViewState) => void;
  clearPersistedState: (storageKey: string) => void;
};

const EtreePersistenceContext = React.createContext<EtreePersistenceContextValue | undefined>(
  undefined
);

export const EtreePersistenceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const loadPersistedState = React.useCallback((storageKey: string): PersistedEtreeViewState | null => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (!stored) return null;
      return JSON.parse(stored) as PersistedEtreeViewState;
    } catch {
      return null;
    }
  }, []);

  const persistState = React.useCallback(
    (storageKey: string, state: PersistedEtreeViewState): void => {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(state));
      } catch {
        // Ignore storage failures (private mode / quota exceeded)
      }
    },
    []
  );

  const clearPersistedState = React.useCallback((storageKey: string): void => {
    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      // Ignore removal failures
    }
  }, []);

  const value: EtreePersistenceContextValue = {
    loadPersistedState,
    persistState,
    clearPersistedState,
  };

  return (
    <EtreePersistenceContext.Provider value={value}>{children}</EtreePersistenceContext.Provider>
  );
};

export const useEtreePersistence = (): EtreePersistenceContextValue => {
  const context = React.useContext(EtreePersistenceContext);
  if (!context) {
    throw new Error("useEtreePersistence must be used within EtreePersistenceProvider");
  }
  return context;
};

export default EtreePersistenceContext;
